<template>
  <section id="form-navigation" class="wizard">
    <section>
      <!-- Mobile Dropdown -->
      <div class="mobile-dropdown">
        <div class="mobile-dropdown__label">
          <label>Current Step</label>
          <p>Step {{ currentPageLinkIndex + 1 }} of {{ links.length }}</p>
        </div>
        <div class="mobile-dropdown__display" @click="toggleDropdown()">
          <p>{{ links[currentPageLinkIndex].linkTitle }}</p>
          <svg
            :class="{
              'mobile-dropdown-show': mobileDropdownShow,
            }"
          >
            <use xlink:href="/assets/public/images/icons.svg#down-arrow" />
          </svg>
        </div>
      </div>
      <div
        class="step-indicator fr-view"
        :class="{
          'mobile-dropdown-show': mobileDropdownShow,
        }"
      >
        <ul>
          <li
            v-for="(link, index) in links"
            :class="{
              active: link.pages.includes(currentPage) && !link.disabled && !disabled,
              'active-disabled': link.pages.includes(currentPage) && (link.disabled || disabled),
              completed: index < currentPageLinkIndex,
              inactive: index > currentPageLinkIndex && !(link.disabled || disabled),
              'not-clickable': index > currentPageLinkIndex && (link.disabled || disabled),
              disabled: !link.pages.includes(currentPage) && (link.disabled || disabled),
              'second-last-child': index === links.length - 2 && nextPageLinkObject.pages.includes(currentPage),
            }"
            :key="index"
            @click.prevent="displayPage(link.pages[0], link.disabled || disabled)"
          >
            <button
              class="after no-hover-focus non-button-style"
              :disabled="link.disabled || disabled"
              :aria-label="getStepAriaLabel(link.pages[0], link.linkTitle, link.disabled)"
            >
              {{ link.linkTitle }}
            </button>
          </li>
        </ul>
      </div>
    </section>
    <hr v-if="showSeparator" id="mobile-separator" />
  </section>
</template>

<script>
export default {
  name: 'MultiStepFormNavigation',

  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    links: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    showSeparator: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data() {
    return {
      mobileDropdownShow: false,
    };
  },

  computed: {
    currentPageLinkIndex() {
      let currentPageLinkIndex = 0;

      this.links.find((link, index) => {
        currentPageLinkIndex = index;
        return link.pages.includes(this.currentPage);
      });

      return currentPageLinkIndex;
    },

    previousPageLinkObject() {
      return this.currentPageLinkIndex - 1 <= 0 ? this.links[0] : this.links[this.currentPageLinkIndex - 1];
    },

    nextPageLinkObject() {
      return this.currentPageLinkIndex + 1 >= this.links.length ? this.links[this.links.length - 1] : this.links[this.currentPageLinkIndex + 1];
    },
  },

  methods: {
    /**
     * Emits an event that requests the parent to display the form page indicated by the step number.
     */
    displayPage(step, disabled) {
      // Disable the ability to click on a link if the link is not active.
      if (!disabled) {
        this.$emit('linkClicked', step);
      }
    },
    /**
     * Generate aria-label for each step
     */
    getStepAriaLabel(step, stepName, isDisabled) {
      let stepAriaLabel = '';

      if (step < this.currentPage) {
        stepAriaLabel = `${stepName} - step completed`;
      } else if (step === this.currentPage) {
        stepAriaLabel = `${stepName} - current step`;
      } else if (isDisabled === true) {
        // for disabled button
        stepAriaLabel = `${stepName} - inactive step`;
      } else {
        // default value
        stepAriaLabel = `${stepName} - active step`;
      }

      return stepAriaLabel;
    },

    toggleDropdown(isShow) {
      if (isShow) this.mobileDropdownShow = isShow;
      else this.mobileDropdownShow = !this.mobileDropdownShow;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@general/styles/_member_portal_imports.scss';

.wizard {
  background: none;
  left: 0;
  right: 0;
  margin-left: 0;
  margin-right: 0;
  padding: 0;

  // desktop view
  section {
    box-shadow: none;
    display: block;
    padding: 0;

    .step-indicator {
      @include flex-row;
      background-color: $WHITE;
      width: inherit;

      @include mobile {
        display: none;

        &.mobile-dropdown-show {
          display: block;
        }
      }

      ul {
        @include flex-col;
        width: 100%;

        li {
          @include flex-row;
          box-sizing: border-box;
          margin: 0;
          padding: $MD $MD $MD calc(#{$MD} + 24px + #{$MD}) !important;
          min-height: 56px;
          cursor: pointer;
          border-left: 2px solid $PRIMARY_GREY_40;
          align-items: center;
          background-color: $NEUTRAL_WHITE;
          position: relative;

          &::before {
            content: '';
            width: 24px;
            height: 24px;
            border-radius: $ROUND;
            box-sizing: border-box;
            border: 1.5px solid $PRIMARY_MEDIUM_GREEN_100;
            background-repeat: no-repeat;
            background-position: center;
            background-size: 24px 24px;
            position: absolute;
            left: $MD;
            top: 50%;
            transform: translateY(-50%);
            pointer-events: none;
          }

          button {
            color: $NEUTRAL_MIDNIGHT;
            text-align: left;
            text-decoration: none;
          }

          &.inactive:hover:not(.disabled):not(.not-clickable):not(.active):not(.active-disabled):not(.completed) {
            background-color: $PRIMARY_BRIGHT_GREEN_20;

            &::before {
              border-color: $PRIMARY_GREY_60;
            }
          }

          &.completed {
            background-color: $NEUTRAL_WHITE;
            border-left-color: $PRIMARY_GREY_40;

            &::before {
              border-color: transparent;
              background-image: url('/assets/public/images/icon_progress_stepper_completed.svg');
            }
          }

          &.active {
            background-color: $PRIMARY_BRIGHT_GREEN_20;
            border-left-color: $PRIMARY_MEDIUM_GREEN_100;

            &::before {
              border-color: transparent;
              background-image: url('/assets/public/images/icon_progress_stepper_active.svg');
            }
          }

          &.active-disabled {
            background-color: $PRIMARY_EGGSHELL_100;
            border-left-color: $PRIMARY_GREY_40;
            cursor: not-allowed;

            &::before {
              border-color: transparent;
              background-image: url('/assets/public/images/icon_progress_stepper_active_disabled.svg');
            }
          }

          &.disabled,
          &.not-clickable {
            background-color: $PRIMARY_EGGSHELL_100;
            border-left-color: $PRIMARY_GREY_40;
            cursor: not-allowed;

            &::before {
              border-color: $PRIMARY_GREY_40;
              background-image: none;
            }

            button {
              color: $PRIMARY_GREY_60;
              font-weight: $FONT_WEIGHT_MEDIUM;
            }
          }
        }
      }
    }
  }

  // mobile view
  @include mobile {
    padding-top: 1.5rem;
    margin-bottom: 2rem !important;
  }
}

// Mobile View Styling
.mobile-dropdown {
  display: none;

  @include mobile {
    display: block;
  }

  &__label {
    @include flex-row;
    justify-content: space-between;
    margin-bottom: 0.5rem;

    label {
      font-weight: $BOLD;
    }

    p {
      margin: 0;
      color: $GREY_600;
    }
  }

  &__display {
    @include flex-row;
    padding: $MD;
    border: 1.5px solid $PRIMARY_GREY_40;
    background-color: $PRIMARY_BRIGHT_GREEN_20;
    border-left: 2px solid $PRIMARY_MEDIUM_GREEN_100;
    font-weight: $FONT_WEIGHT_STRONG;
    cursor: pointer;
    column-gap: $MD;
    align-items: center;

    &::before {
      content: '';
      width: 24px;
      height: 24px;
      border-radius: $ROUND;
      background: url('/assets/public/images/icon_progress_stepper_active.svg') center/24px 24px no-repeat;
    }

    p {
      margin: 0;
      line-height: 1.5rem;
    }

    svg:last-child {
      pointer-events: none;
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-50%, -50%);
      width: 1.5rem;
      height: 1.5rem;
      color: $GREY_600;

      &.mobile-dropdown-show {
        transform: translate(-50%, -50%) rotate(180deg);
      }
    }
  }
}

#mobile-separator {
  display: none;

  @include mobile {
    display: block;
    left: -$MOBILE_PADDING;
    border: none;
    height: 1.5rem;
    width: 100vw;
    height: 50px;
    margin-top: 0;
    box-shadow: 0px 4px 4px rgba($BLACK, 0.06);
  }
}
</style>
